/**
 * Named bundles of columns, offered as a bulk tick.
 *
 * ## Additive, always, and that is the whole safety argument
 *
 * Picking a set **turns its columns on and touches nothing else.** Nothing is
 * removed, nothing is reordered, and nothing a user arranged is overwritten —
 * so a set cannot interact badly with a screen somebody built, and undoing one
 * is unticking what you did not want rather than a separate revert.
 *
 * That rule is what keeps a set from being a worse version of something the app
 * already has. A bundle that *replaced* the column list would be a screen, and
 * screens exist: the club's and the product's, as tabs, linked, with the
 * member's own layer over them. This is the lighter thing — the column list's
 * bulk tick, and nothing more.
 *
 * ## A field a screen does not have is skipped, not an error
 *
 * The universe differs by screen and by market: a set naming `rsRank` on a
 * screen that does not carry it must add what it can and stay quiet about the
 * rest. A set is a suggestion about columns, not an assertion that they exist —
 * so a screen added upstream tomorrow starts appearing in its set with nothing
 * here being taught about it.
 *
 * ## Why these live in code
 *
 * A static map, editable in a pull request, with no schema and no server. The
 * moment a *club* wants its own bundle the shape is already a published screen
 * and belongs in `screener_preset` — sets are for what everybody means by
 * "the momentum columns", which does not vary by who is asking.
 */

import type { ScreenerColumn } from "@qufolio/model";

import { showColumns, type UniverseColumn } from "./column-universe.js";

export interface ColumnSet {
  id: string;
  label: string;
  /** What it turns on, in the order it turns them on. */
  fields: readonly string[];
}

/**
 * The bundles, in the order the menu offers them.
 *
 * Field names are the server's own, normalised camelCase — see
 * `screener_columns.py`. Each set is what somebody would ask for out loud,
 * which is the test for whether it should exist at all: "the fundamentals",
 * "the moving averages". A set nobody would name is a column list.
 */
export const COLUMN_SETS: readonly ColumnSet[] = [
  {
    id: "identity",
    label: "Identity",
    fields: ["logo", "symbol", "name", "sector", "industry", "exchange", "country"],
  },
  {
    id: "price",
    label: "Price & move",
    fields: ["open", "high", "low", "close", "prevClose", "change", "changePercent"],
  },
  {
    id: "volume",
    label: "Volume",
    fields: ["volume", "rvol", "obv", "obvEma", "obvUpSince", "obvDownSince"],
  },
  {
    id: "size",
    label: "Size & profile",
    fields: ["marketCap", "beta", "lastAnnualDividend", "isEtf", "isFund", "isActivelyTrading"],
  },
  {
    id: "averages",
    label: "Moving averages",
    fields: ["sma17", "sma50Support", "sma200", "wma30", "sma17Support", "sma200Support"],
  },
  {
    id: "strength",
    label: "Relative strength",
    fields: ["rs", "rsEma14", "rsRank", "rsUpSince", "rsDownSince", "rankNh", "SPY"],
  },
  {
    id: "crosses",
    label: "Crosses",
    fields: [
      "sma17CrossUp",
      "sma50CrossUp",
      "sma200CrossUp",
      "sma17CrossDown",
      "sma50CrossDown",
      "sma200CrossDown",
    ],
  },
  {
    id: "signals",
    label: "Signals",
    fields: ["bongoUp", "bongoDown", "kahunaUp", "kahunaDown", "dotsUpCount", "dotsDownCount"],
  },
  {
    id: "levels",
    label: "Levels",
    fields: [
      "tlSupportTouch",
      "tlSupportBreak",
      "tlResistanceTouch",
      "tlResistanceBreak",
      "wh1",
      "wl1",
    ],
  },
];

/**
 * Turn a set's columns on, leaving everything else exactly as it was.
 *
 * Through `showColumns`, which shares its one pass with the tick beside it — so
 * there is one answer to "what does showing a column mean", and that answer is
 * not trivial: an unconfigured field gains an entry, a hidden one loses its
 * `hide`, and a shown one keeps its place rather than moving to the end.
 *
 * **Not `toggleColumn` per field.** That rebuilds the list from the universe's
 * own `visible` flags, which are a snapshot — so a loop re-hides everything the
 * previous iteration turned on, and a six-column set adds one column.
 *
 * **A field the screen does not carry is skipped in silence.** The universe
 * differs per screen and per market, and a set is a suggestion about columns
 * rather than a claim that they exist.
 *
 * Returns the input **by identity** when the set adds nothing — every column was
 * already shown, or none of them exists here — so a second pick costs neither a
 * re-render nor a save.
 */
export function applyColumnSet(
  universe: readonly UniverseColumn[],
  columns: readonly ScreenerColumn[],
  set: ColumnSet,
): readonly ScreenerColumn[] {
  const present = new Set(universe.map((u) => u.field));
  const adding = set.fields.filter((f) => present.has(f));
  // Identity when the set adds nothing — every column already shown, or none of
  // them on this screen — so a second pick costs neither a render nor a save.
  if (pendingInSet(universe, set) === 0) return columns;
  return showColumns(universe, columns, adding);
}

/** How many of a set's columns this screen could still turn on. */
export function pendingInSet(
  universe: readonly UniverseColumn[],
  set: ColumnSet,
): number {
  const present = new Map(universe.map((u) => [u.field, u]));
  return set.fields.filter((f) => present.get(f)?.visible === false).length;
}
